import { PortalBrand } from "@/pages/PortalLogin";
import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, MapPin, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, downloadBlob, applyBrand } from "@/lib/api";
import { ComparisonView } from "@/components/ComparisonView";

export default function PortalCompare() {
  const { id } = useParams();
  const [me, setMe] = useState(null);
  const [c, setC] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/portal/me").then((r) => { setMe(r.data); applyBrand(r.data.brand); }).catch(() => setMe(false));
    api.get(`/portal/comparisons/${id}`).then((r) => setC(r.data)).catch(() => setError("Comparison not found."));
  }, [id]);

  if (me === false) return <Navigate to="/portal/login" replace />;
  if (error) return <div className="min-h-screen grid place-items-center text-center px-4" data-testid="portal-compare-error"><div><Lock className="h-8 w-8 text-amber-400 mx-auto mb-3" /><p className="text-slate-300 mb-4">{error}</p><Link to="/portal" className="text-emerald-400 underline">Back to portal</Link></div></div>;
  if (!me || !c) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>;
  const pdf = async () => {
    setBusy(true);
    try { await downloadBlob(`/portal/comparisons/${id}/pdf`, `${(c.name || "franchise-comparison").normalize("NFKD").replace(/[^\w]+/g, "-").slice(0, 60)}.pdf`); }
    catch { toast.error("PDF download failed"); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen" data-testid="portal-compare-page">
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/portal" data-testid="portal-compare-back" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" />My portal</Link>
          <PortalBrand branding={me} compact />
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div><p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-2">Franchise comparison</p>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="portal-compare-title">{c.name}</h1>
            <p className="flex items-center gap-1 text-sm text-cyan-300 mt-2"><MapPin className="h-4 w-4" />{c.address}</p></div>
          <Button data-testid="portal-compare-pdf" disabled={busy} onClick={pdf} className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Download PDF</Button>
        </div>
        <ComparisonView c={c} />
        <p className="text-xs text-slate-500 text-center pt-10">Prepared by {c.prepared_by || me.company_name} · {me.company_name}</p>
      </main>
    </div>
  );
}
